import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { AdSpace } from './AdSpace';
import { LifeArea } from './WheelOfLife';
import { trackFormCompletion } from '../analytics';
import { Pencil, Check, X } from 'lucide-react';

interface LifeAreaFormProps {
  lifeAreas: LifeArea[];
  onUpdateRating: (id: string, rating: number) => void;
  onUpdateColor?: (id: string, color: string) => void;
  onUpdateName?: (id: string, name: string) => void;
  onUpdateDescription?: (id: string, description: string) => void;
  onComplete: () => void;
}

export function LifeAreaForm({ lifeAreas, onUpdateRating, onUpdateColor, onUpdateName, onUpdateDescription, onComplete }: LifeAreaFormProps) {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const getRatingLabel = (rating: number) => {
    if (rating <= 2) return t('ratings.veryDissatisfied');
    if (rating <= 4) return t('ratings.dissatisfied');
    if (rating <= 6) return t('ratings.neutral');
    if (rating <= 8) return t('ratings.satisfied');
    return t('ratings.verySatisfied');
  };

  const getRatingColor = (rating: number) => {
    if (rating <= 3) return 'text-red-500';
    if (rating <= 5) return 'text-orange-500';
    if (rating <= 7) return 'text-yellow-500';
    return 'text-green-500';
  };

  const handleComplete = (e: React.FormEvent) => {
    e.preventDefault();
    trackFormCompletion();
    onComplete();
  };

  const startEditing = (area: LifeArea) => {
    setEditingId(area.id);
    setEditName(area.name);
    setEditDescription(area.description);
  };

  const saveEditing = (id: string) => {
    if (onUpdateName) onUpdateName(id, editName);
    if (onUpdateDescription) onUpdateDescription(id, editDescription);
    setEditingId(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  return (
    <form onSubmit={handleComplete} className="space-y-6" aria-labelledby="assessment-title">
      <Card>
        <CardHeader>
          <CardTitle id="assessment-title">{t('form.title')}</CardTitle>
          <p className="text-muted-foreground" id="assessment-description">
            {t('form.description')}
          </p>
        </CardHeader>
      </Card>

      <fieldset 
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
        aria-labelledby="assessment-title"
        aria-describedby="assessment-description"
      >
        <legend className="sr-only">Life Areas Assessment</legend>
        {lifeAreas.map((area, index) => (
          <Card key={area.id} className="p-6" role="group" aria-labelledby={`area-${area.id}-label`}>
            <div className="space-y-4">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  {editingId === area.id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value.slice(0, 30))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveEditing(area.id);
                          } else if (e.key === 'Escape') {
                            cancelEditing();
                          }
                        }}
                        maxLength={30}
                        className="w-full px-2 py-1 text-base font-medium border rounded"
                        placeholder="Area name"
                      />
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            e.preventDefault();
                            saveEditing(area.id);
                          } else if (e.key === 'Escape') {
                            cancelEditing();
                          }
                        }}
                        className="w-full px-2 py-1 text-sm text-muted-foreground border rounded resize-none"
                        rows={2}
                        placeholder="Description"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-base" id={`area-${area.id}-label`}>
                        {area.name}
                      </Label>
                      <p 
                        className="text-sm text-muted-foreground mt-1" 
                        id={`area-${area.id}-description`}
                      >
                        {area.description}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {editingId === area.id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => saveEditing(area.id)}
                        className="p-1 hover:bg-green-100 rounded"
                        aria-label="Save changes"
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditing}
                        className="p-1 hover:bg-red-100 rounded"
                        aria-label="Cancel editing"
                      >
                        <X className="h-4 w-4 text-red-600" />
                      </button>
                    </>
                  ) : (
                    onUpdateName && onUpdateDescription && (
                      <button
                        type="button"
                        onClick={() => startEditing(area)}
                        className="p-1 hover:bg-gray-100 rounded"
                        aria-label={`Edit ${area.name}`}
                      >
                        <Pencil className="h-4 w-4 text-gray-600" />
                      </button>
                    )
                  )}
                  
                  {/* Color Picker */}
                  {onUpdateColor && (
                    <input
                      type="color"
                      value={area.color || '#3b82f6'}
                      onChange={(e) => onUpdateColor(area.id, e.target.value)}
                      className="w-10 h-10 border border-border cursor-pointer rounded"
                      aria-label={`Choose color for ${area.name} area`}
                      title={`Choose color for ${area.name}`}
                    />
                  )}
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{t('form.rating')}</span>
                  <div className="text-right">
                    <span 
                      className="text-2xl font-bold" 
                      aria-live="polite"
                      aria-label={`${area.name} rating: ${area.rating} out of 10`}
                    >
                      {area.rating}
                    </span>
                    <span className="text-sm text-muted-foreground">/10</span>
                  </div>
                </div>
                
                <Slider
                  value={[area.rating]}
                  onValueChange={(value) => onUpdateRating(area.id, value[0])}
                  max={10}
                  min={1}
                  step={1}
                  className="w-full"
                  aria-labelledby={`area-${area.id}-label`}
                  aria-describedby={`area-${area.id}-description`}
                  aria-valuetext={`${area.rating} out of 10`}
                />
                
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{t('form.veryLow')}</span>
                  <span 
                    className={getRatingColor(area.rating)}
                    aria-live="polite"
                    aria-label={`${area.name} satisfaction level: ${getRatingLabel(area.rating)}`}
                  >
                    {getRatingLabel(area.rating)}
                  </span>
                  <span>{t('form.excellent')}</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </fieldset>

      {/* Inline Ad - Natural break before completion */}
      {/* <AdSpace position="inline" className="my-8" /> */}

      <div className="text-center">
        <Button 
          type="submit"
          size="lg" 
          className="px-8"
          aria-describedby="complete-help"
        >
          {t('form.viewWheel')}
        </Button>
        <p id="complete-help" className="sr-only">
          Click to view your completed wheel of life chart with all your ratings
        </p>
      </div>
    </form>
  );
}